import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Activity } from "lucide-react";
import { useSite, tx } from "@/lib/lang";
import { scrollToSection } from "@/hooks/useLenis";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1 -mb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { content, lang } = useSite();
  const hero = content.hero || {};
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="hero" data-testid="hero-section" ref={sectionRef} className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0 hero-grid-bg" />
      <motion.div
        style={{ y: glowY }}
        className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full bg-royal/25 blur-[140px] pointer-events-none"
      />
      <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-azure/10 blur-[120px] pointer-events-none" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 pt-36 pb-24 grid lg:grid-cols-[1.15fr_0.85fr] gap-16 items-center w-full">
        <div>
          <h1 data-testid="hero-payoff-heading" className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-ice">
            <Line delay={0.15}>{tx(hero.line1, lang)}</Line>
            <Line delay={0.28}>
              <span className="text-strike text-muted-foreground">{tx(hero.line2, lang)}</span>
            </Line>
            <Line delay={0.41}>{tx(hero.line3, lang)}</Line>
            <Line delay={0.54}>
              <span className="font-serif italic font-semibold text-azure">{tx(hero.line4, lang)}</span>
            </Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-8 max-w-xl text-base text-muted-foreground leading-relaxed"
          >
            {tx(hero.subtitle, lang)}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-audit-cta"
              onClick={() => scrollToSection("#contatti")}
              className="group inline-flex items-center gap-2 bg-royal hover:bg-azure hover:text-[#0B1424] transition-colors text-white font-semibold px-6 py-3.5 rounded-full"
            >
              {tx(hero.cta_primary, lang)}
              <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-method-cta"
              onClick={() => scrollToSection("#metodo")}
              className="inline-flex items-center gap-2 border border-ice/15 hover:border-azure/60 hover:text-azure transition-colors text-ice/90 font-medium px-6 py-3.5 rounded-full"
            >
              {tx(hero.cta_secondary, lang)}
              <ArrowDown size={16} />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-14 max-w-md"
          >
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-azure border-l-2 border-royal pl-4">
              {tx(hero.caption, lang) ||
                (lang === "it"
                  ? "Execution, non solo diagnosi: dalla profilazione all'attivazione, con una gestione continuativa."
                  : "Execution, not just diagnosis: from profiling to activation, with ongoing management.")}
            </p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="relative hidden lg:block"
        >
          <motion.div style={{ y: imgY }} className="relative rounded-2xl overflow-hidden border border-ice/10 aspect-[4/5]">
            <img
              src={hero.image || "https://images.unsplash.com/photo-1760246964044-1384f71665b9?fm=jpg&q=80&w=1600&auto=format&fit=crop"}
              alt="Edificio moderno al tramonto con luci accese"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1424]/70 via-transparent to-transparent" />
            <div className="absolute inset-0 bg-royal/15 mix-blend-multiply" />
          </motion.div>
          <div className="absolute -left-8 bottom-12 bg-surface/90 backdrop-blur-md border border-royal/30 rounded-xl px-5 py-4 animate-float-slow" data-testid="hero-matching-badge">
            <div className="flex items-center gap-2 text-azure">
              <Activity size={16} />
              <span className="font-mono text-xs uppercase tracking-[0.2em]">{lang === "it" ? "Matching orario 24/7" : "Hourly matching 24/7"}</span>
            </div>
            <p className="font-display text-2xl font-bold text-ice mt-1">84%</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
